import {
    CLEAR_INVALID_LOGIN,
    CLIENT_PRODUCTS_LOADING,
    ERROR_CLIENT_PAYMENT,
    FETCH_CLIENT_PAYMENT,
    INVALID_LOGIN,
    SET_ACCESS_TOKEN,
    SET_AUTH,
    SET_BILLING_DOMAIN,
    SET_CLIENT_PAYMENT,
    SET_CLIENT_PERSONAL,
    SET_CLIENT_PRODUCTS,
    SET_CONTRACT_PERIOD,
    SET_LOGIN_ERROR,
    SET_REFRESH_TOKEN,
    SET_RECEIPT_INFO,
    CLIENT_CONTACTS_LOADING,
    SET_CLIENT_CONTACTS,
    SET_MIGRATION_STATUS,
} from '../actionTypes/auth';
import { HYDRATE } from 'next-redux-wrapper';
import cookie from 'react-cookies';
import { isClientSide } from '~/src/helpers/ssr';

export interface IInvalidLoginDefault {
    count: number,
    expires: Date | null
}

export interface IAuthState {
    isAuth: boolean,
    accessToken: string,
    refreshToken: string,
    loginError: string | null,
    clientPersonal: IClientPersonal | null,
    clientPaymentFetching: boolean,
    clientPayment: IClientPayment | null,
    clientProducts: IClientProducts | null,
    clientContacts: IClientContacts | null,
    productType: number,
    invalidLogin: IInvalidLoginDefault,
    balanceLimitInfo: any,
    billingDomain: string,
    isClientProductsLoading: boolean,
    isClientContactsLoading: boolean,
    clientPaymentLoaded: number | null,
    clientContractPeriods: IContractPeriod | null
    receiptInfo: IReceipt | null,
    migrationStatus?: number,
}

export interface IAddress {
    street: string,
    streetId: number,
    house: number,
    building: string,
    flat: number
}

export interface IClientContacts {
    email: IContact[],
    phone: IPhoneContact[]
}

export enum EContactStatuses {
    confirmed = 'confirmed',
    unconfirmed = 'unconfirmed'
}

export interface IContact {
    id: number,
    value: string,
    status: EContactStatuses
}

export interface IPhoneContact extends IContact {
    phoneTypeId: number,
}

export interface IClientPersonal {
    agreementId: number,
    agreement: number,
    agreementTypeId: number,
    fio: string,
    photoUrl: string,
    address: IAddress,
    cityId: number
}

export interface IContractPeriod {
    activeFrom: number,
    activeTo: number,
    advType: number,
    discValue: string,
    duration: string,
    id: number,
    name: string,
    timeLeft: number
    penaltySum: number
}

export interface IClientPayment {
    balance: number,
    paySum: number,
    payDay: string,
    bindingCards: any[],
    promisedPayment: any,
    promisedPaymentInfo: {
        code: any,
        promisedPaymentBal: {
            payForDay: any,
            day: any,
            activeTo: any,
            auto: any
        }
    },
    payText: string,
    payChargesSum: number,
    payTextParam: number,
    payPeriodDates: string,
    payCurBalance: number,
    payIsDeny: boolean,
    isAllow: number
}

export interface IClientService {
    productId: number,
    productName: string,
    planId: number,
    planName: string,
    specification: any,
    terminalResources: any,
    activated: 0 | 1
}

export interface IClientProducts {
    tariffPriceMvno: any[],
    salePackageId: string,
    tariffPrice: number,
    tariffName: string,
    services: IClientService[],
    tariffPriceDiscount: number
}

export interface IReceiptAddress {
    street: string,
    streetId: number,
    house: number,
    building: string,
    flat: number
}

export interface IReceiptBank {
    bank_name: string,
    bic: number
    category: number
    corresp_acc: number
    kpp: number
    name: string,
    payee_inn: number
    pers_acc: number
    personal_acc: number
    size: number
    sum: number
}

export interface ITerminalResource {
    id: number,
    name: string,
    serviceId: number,
    type: string
}

export interface IReceiptProductService {
    activated: number,
    planId: number,
    planName: string,
    productId: number,
    productName: string,
    specification: number,
    terminalResources: ITerminalResource[]
}

export interface IReceiptProducts {
    salePackageId: number,
    services: IReceiptProductService[],
    tariffName: string,
    tariffPrice: number,
    tariffPriceMvno: null | any[]
}

export interface IReceiptPlan {
    name: string,
    speed: string
}

export interface IHeaderInfo {
    AgrDate: string,
    AgrNumber: number,
    AgrPayTypeName: string,
    AgreementTypeName: string,
    ClientName: string,
    PeriodRow: string,
    PlansList: string,
    plans: IReceiptPlan[]
}

export interface IPeriodChargeSubcategory {
    SubcategoryId: number,
    SubcategoryName: string,
    PlanName?: string,
    SubcategorySum: number,
    SubcategorySaleSum: number,
    SubcategoryTotalSum: number,
    SubcategoryRa?: [
        {
            ra: string,
            open: number,
            RaActiveFrom: string,
            RaActiveTo: string
        }
    ]
}

export interface IPeriodChargeCategory {
    subcategories: IPeriodChargeSubcategory[],
    CategoryId: number,
    CategoryName: string,
    CategorySum: number,
    CategorySaleSum: number,
    CategoryTotalSum: number,
    CategoryNote: string
}

export interface IPeriodPayment {
    PaymentDate: string,
    PaymentSum: number,
    PaymentSource: string,
    ChequeUrl: string
}

export interface IReceiptHistory {
    AdditionalInfo: {
        BalBeg: number,
        BalEnd: number
    }
    DateFrom: string,
    DateTo: string,
    FullDisplay: boolean,
    GetTime: string,
    HeaderInfo: IHeaderInfo,
    PeriodCharges: {
        categories: IPeriodChargeCategory[],
        PeriodSum: number,
        PeriodSaleSum: number,
        PeriodTotalSum: number
    },
    PeriodPayments: {
        payments: IPeriodPayment[],
        PaymentsSum: number
    }
}

export interface IReceipt {
    address: IReceiptAddress,
    bank: IReceiptBank,
    products: IReceiptProducts,
    balEnd: number,
    history: IReceiptHistory,
    bankName: string,
    bik: number,
    companyInn: number,
    companyKpp: number,
    companyName: string
    correspondentAccount: number,
    director: string,
    fax: string,
    fisAddress: string,
    glavBuchgalter: string,
    internetSpeed: string,
    isItFlaotingPayScheme: boolean,
    kassaAddress: string,
    legalAddress: string,
    payDay: number,
    qr: string,
    settlementAccount: number
}

const INVALID_LOGIN_DEFAULT: IInvalidLoginDefault = {
    count: 0,
    expires: null,
};

const initialState: IAuthState = {
    isAuth: false,
    accessToken: '',
    refreshToken: '',
    loginError: '',
    clientPersonal: null,
    clientPaymentFetching: false,
    clientPaymentLoaded: null,
    clientPayment: null,
    clientProducts: null,
    clientContacts: null,
    productType: 0,
    invalidLogin: INVALID_LOGIN_DEFAULT,
    balanceLimitInfo: {},
    billingDomain: '',
    isClientProductsLoading: false,
    isClientContactsLoading: false,
    clientContractPeriods: null,
    receiptInfo: null,
};

const INVALID_LOGIN_COOKIE_KEY = 'invalidLogin';
const EXPIRE_LOGIN_COUNT_TIME = 3600000;

function invalidLoginFromCookie(): IInvalidLoginDefault {
    const invalidLoginCookie = cookie.load(INVALID_LOGIN_COOKIE_KEY);
    if (invalidLoginCookie == null) {
        return INVALID_LOGIN_DEFAULT;
    }
    return {
        count: invalidLoginCookie.count,
        expires: new Date(invalidLoginCookie.expires),
    };
}

export default (state = initialState, action: any) => {
    switch (action.type) {
        case HYDRATE:
            return {
                ...state,
                ...action.payload.auth,
                invalidLogin: invalidLoginFromCookie(),
            };
        case CLEAR_INVALID_LOGIN:
            if (isClientSide()) {
                setTimeout(() => {
                    cookie.remove(INVALID_LOGIN_COOKIE_KEY);
                }, 15);
            }
            return {
                ...state,
                invalidLogin: INVALID_LOGIN_DEFAULT,
            };
        case INVALID_LOGIN: {
            const expiresDate = new Date(
                (new Date()).getTime() + EXPIRE_LOGIN_COUNT_TIME,
            );
            const invalidLogin = {
                count: state.invalidLogin.count + 1,
                expires: expiresDate,
            };
            cookie.save(
                INVALID_LOGIN_COOKIE_KEY,
                invalidLogin,
                {
                    expires: expiresDate,
                },
            );
            return {
                ...state,
                invalidLogin,
            };
        }
        case SET_AUTH: {
            return {
                ...state,
                isAuth: action.payload,
            };
        }

        case SET_ACCESS_TOKEN: {
            if (isClientSide()) {
                cookie.remove(INVALID_LOGIN_COOKIE_KEY);
            }
            return {
                ...state,
                accessToken: action.payload,
                invalidLoginCount: 0,
            };
        }

        case SET_REFRESH_TOKEN: {
            return {
                ...state,
                refreshToken: action.payload,
            };
        }

        case SET_MIGRATION_STATUS: {
            return {
                ...state,
                migrationStatus: action.payload,
            };
        }

        case SET_LOGIN_ERROR: {
            return {
                ...state,
                loginError: action.payload,
            };
        }

        case SET_CLIENT_PERSONAL: {
            return {
                ...state,
                clientPersonal: action.payload,
            };
        }

        case FETCH_CLIENT_PAYMENT: {
            return {
                ...state,
                clientPaymentFetching: true,
            };
        }

        case SET_CLIENT_PAYMENT: {
            return {
                ...state,
                clientPaymentFetching: false,
                clientPayment: action.payload,
                clientPaymentLoaded: (new Date()).getTime()
            };
        }

        case ERROR_CLIENT_PAYMENT: {
            return {
                ...state,
                clientPaymentFetching: false,
            };
        }

        case SET_CLIENT_PRODUCTS: {
            return {
                ...state,
                clientProducts: {
                    ...action.payload,
                    loaded: (new Date()).getTime(),
                },
            };
        }

        case CLIENT_PRODUCTS_LOADING:
            return {
                ...state,
                isClientProductsLoading: action.payload,
            };

        case SET_CLIENT_CONTACTS: {
            return {
                ...state,
                clientContacts: {
                    ...action.payload,
                    loaded: (new Date()).getTime(),
                },
            };
        }

        case CLIENT_CONTACTS_LOADING:
            return {
                ...state,
                isClientContactsLoading: action.payload,
            };

        case SET_BILLING_DOMAIN: {
            return {
                ...state,
                billingDomain: action.payload,
            };
        }

        case SET_CONTRACT_PERIOD: {
            return {
                ...state,
                clientContractPeriods: action.payload
            };
        }

        case SET_RECEIPT_INFO: {
            return {
                ...state,
                receiptInfo: action.payload
            };
        }

        default: {
            return state;
        }
    }
};
